/**
 * Knowledge da fonte unica (QA26-F5.1, STD-6 punto 3): il manuale per ruolo e le regole di docs/knowledge/ servono la
 * Guida (persone), le instructions dell'MCP e l'Assistente Qualità (AI). I testi stanno in generated/knowledge.ts,
 * rigenerato da scripts/build-knowledge.mjs (`pnpm knowledge`); il test knowledge.test.ts fallisce se non è allineato.
 */
import { KNOWLEDGE, KNOWLEDGE_EN, MANUALE, MANUALE_EN } from "./generated/knowledge";
import type { Lingua } from "./errori";
import { RUOLI, type Ruolo } from "./dominio";

export { KNOWLEDGE, KNOWLEDGE_EN, MANUALE, MANUALE_EN };

export type SezioneGuidaId = Ruolo | "panoramica";

export interface SezioneGuida {
  id: SezioneGuidaId;
  titolo: string;
  testo: string;
  /** true = riguarda il ruolo dell'utente: sta in cima e parte aperta. */
  mia: boolean;
}

const TITOLI: Record<Lingua, Record<SezioneGuidaId, string>> = {
  it: {
    commerciale: "Segnalare e tenere aggiornato",
    qualita: "Classificare e lavorare le NC",
    amministrazione: "Claim e costo della non qualità",
    direzione: "Cruscotto e report",
    admin: "Proposte, accessi, liste e criteri",
    panoramica: "Panoramica, glossario e domande frequenti",
  },
  en: {
    commerciale: "Reporting and keeping up to date",
    qualita: "Classifying and working NCs",
    amministrazione: "Claims and cost of poor quality",
    direzione: "Dashboard and report",
    admin: "Proposals, access, lists and criteria",
    panoramica: "Overview, glossary and FAQ",
  },
};

const manualeDi = (lingua: Lingua) => (lingua === "en" ? MANUALE_EN : MANUALE);

/**
 * Sezioni che un ruolo legge nella Guida. Il commerciale non riceve le sezioni di amministrazione e qualità, che
 * parlano di claim e schede fornitore (fuori dal suo perimetro); gli altri leggono anche le sezioni dei ruoli con cui
 * lavorano.
 */
export const SEZIONI_LEGGIBILI: Record<Ruolo, readonly Ruolo[]> = {
  admin: RUOLI,
  direzione: ["direzione", "commerciale", "qualita", "amministrazione"],
  qualita: ["qualita", "commerciale", "amministrazione"],
  amministrazione: ["amministrazione", "qualita"],
  commerciale: ["commerciale"],
};

/** Toglie il titolo «# ...» in testa: nel pannello il titolo è quello della sezione. */
const corpo = (md: string) => md.replace(/^# [^\n]*\n+/, "");

/**
 * Ordine della Guida: le sezioni dei propri profili e la panoramica (aperte), poi le altre leggibili da almeno uno dei
 * profili (chiuse). Senza profili Red Flag (solo ruoli NC) la Guida ha la panoramica.
 */
export function sezioniGuida(profili: readonly Ruolo[], lingua: Lingua = "it"): SezioneGuida[] {
  const manuale = manualeDi(lingua);
  const make = (id: SezioneGuidaId, mia: boolean): SezioneGuida => ({
    id,
    titolo: TITOLI[lingua][id],
    testo: corpo(id === "panoramica" ? manuale.index : manuale[id]),
    mia,
  });
  const mie = RUOLI.filter((r) => profili.includes(r));
  const leggibili = RUOLI.filter((r) => !mie.includes(r) && mie.some((m) => SEZIONI_LEGGIBILI[m].includes(r)));
  return [...mie.map((r) => make(r, true)), make("panoramica", true), ...leggibili.map((r) => make(r, false))];
}

/** Tutto il manuale in un testo (knowledge dell'Assistente). */
export function manualeCompleto(lingua: Lingua = "it"): string {
  const m = manualeDi(lingua);
  return [m.index, ...RUOLI.map((r) => m[r])].join("\n");
}

/** Le regole di docs/knowledge/ in un testo. */
export function regole(lingua: Lingua = "it"): string {
  return (lingua === "en" ? KNOWLEDGE_EN : KNOWLEDGE).map((k) => k.testo).join("\n");
}

/**
 * Instructions dell'MCP: perimetro e regole essenziali. Brevi: Claude le riceve a ogni connessione; il manuale
 * completo e le regole si leggono con lo strumento `guida`.
 */
export function istruzioniMcp(baseUrl: string): string {
  return [
    `Portale Qualità Brandart (${baseUrl}): sezione Red Flag (segnalazioni dei clienti, classificazione, NC con 8D leggero, report settimanale) e sezione Claim fornitori (claim, recupero, blocco pagamenti).`,
    "Gli strumenti rispondono per l'utente collegato e vedono solo quello che lui vede nel portale. Il commerciale non vede claim né schede fornitore. Fuori perimetro la risposta è «non trovata»: non dire se il dato esiste.",
    "Processo: segnalazione → classificazione (proposta, la decide l'admin) → NC (una per tipo NC approvato) → claim → chiusura. Senza classificazione approvata una segnalazione non ha NC.",
    "Regola dell'8D: una NC non si chiude senza azione di lungo termine (D5) e verifica dell'efficacia (D8) con esito «Efficace».",
    "Clienti, valori e claim sono dati commerciali riservati: riportali solo all'utente che li chiede. Sola lettura: per cambiare qualcosa l'utente usa il portale.",
    "Rispondi nella lingua dell'utente. Lo strumento `guida` accetta `lingua` (\"it\" | \"en\") e restituisce manuale e regole in quella lingua.",
  ].join("\n");
}

/** Knowledge completa per l'Assistente Qualità (system prompt lato agent-runtime). */
export function knowledgeAssistente(lingua: Lingua = "it"): string {
  const titoli = lingua === "en" ? ["# Portal manual", "# Rules and edge cases"] : ["# Manuale del portale", "# Regole e casi limite"];
  return [titoli[0], manualeCompleto(lingua), titoli[1], regole(lingua)].join("\n\n");
}
